// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module SharedIndex
 * @package @geenius/analytics
 * @description Root barrel for the authored shared analytics runtime. This
 * entrypoint publishes the package contract consumed by every UI and Convex
 * variant, including types, runtime helpers, errors, validators, and provider
 * implementations without routing through nested barrel chains.
 */

/** Core analytics event, page-view, device, and dashboard contracts shared across every variant. */
/** Extended analytics configuration, funnel, realtime, and chart contracts shared across every variant. */
/** Privacy, cohort, and replay contracts shared across runtime, Storybook, and Convex surfaces. */
export type {
  AnalyticsConfig,
  AnalyticsDashboardData,
  AnalyticsDashboardQuery,
  AnalyticsEventQuery,
  AnalyticsPageViewQuery,
  AnalyticsReadResult,
  AnalyticsStorageAdapter,
  AnalyticsEvent,
  ChartDataPoint,
  CohortAnalysisQuery,
  ConsentCategory,
  CohortAnalysis,
  CohortBucket,
  CohortRetentionPoint,
  ConsentSettings,
  DeviceInfo,
  EventType,
  FunnelResult,
  FunnelResultsQuery,
  FunnelStep,
  FunnelStepResult,
  MetricSummary,
  PageView,
  PrivacyMode,
  PrivacySettings,
  ProviderConfig,
  ProviderName,
  RealtimeStats,
  RealtimeStatsQuery,
  SessionReplayFrame,
  SessionReplaySnapshot,
  TimelineEvent,
  TimeRange,
  TopPageSummary,
  UserTraits,
} from "./types";

import { isDoNotTrackEnabled } from "./privacy";
import type { AnalyticsConfig, DeviceInfo } from "./types";

/** Localized formatting and SEO helpers shared by dashboard and Storybook surfaces. */
export {
  buildAnalyticsSeoMeta,
  formatAnalyticsTime,
  getAnalyticsDeviceLabel,
  pluralizeAnalytics,
  translateAnalytics,
} from "./fsl";

/**
 * Detects the current execution environment's browser and device characteristics.
 *
 * @returns Normalized device information safe to attach to analytics payloads.
 */
export function detectDevice(): DeviceInfo {
  if (typeof navigator === "undefined")
    return {
      browser: "unknown",
      os: "unknown",
      device: "desktop",
      screenWidth: 0,
      screenHeight: 0,
      language: "en",
    };
  const ua = navigator.userAgent;
  const m = /Mobile|Android|iPhone/i.test(ua);
  const t = /Tablet|iPad/i.test(ua);
  const browser = ua.includes("Firefox")
    ? "Firefox"
    : ua.includes("Chrome") && !ua.includes("Edg")
      ? "Chrome"
      : ua.includes("Safari") && !ua.includes("Chrome")
        ? "Safari"
        : ua.includes("Edg")
          ? "Edge"
          : "Other";
  const os = ua.includes("Mac")
    ? "macOS"
    : ua.includes("Windows")
      ? "Windows"
      : ua.includes("Linux")
        ? "Linux"
        : ua.includes("Android")
          ? "Android"
          : "Other";
  return {
    browser,
    os,
    device: m ? "mobile" : t ? "tablet" : "desktop",
    screenWidth: typeof screen !== "undefined" ? screen.width : 0,
    screenHeight: typeof screen !== "undefined" ? screen.height : 0,
    language: navigator.language,
  };
}

/**
 * Generates a best-effort session identifier for analytics events.
 *
 * @returns Session identifier suitable for correlating events in local runtimes.
 */
export function generateSessionId(): string {
  return `ses_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Determines whether analytics dispatch should proceed for the supplied configuration.
 *
 * @param config Analytics configuration that may respect browser tracking preferences.
 * @returns `true` when tracking is allowed in the current environment; otherwise `false`.
 */
export function shouldTrack(config: AnalyticsConfig): boolean {
  if (
    config.respectDnt &&
    typeof navigator !== "undefined" &&
    isDoNotTrackEnabled()
  )
    return false;
  return true;
}

/** Default analytics configuration used when consumers omit optional settings. */
export const defaultAnalyticsConfig: AnalyticsConfig = {
  trackPageViews: true,
  trackClicks: false,
  trackErrors: false,
  trackPerformance: false,
  trackSessionStart: true,
  trackNavigation: true,
  allowPreConsentTracking: false,
  batchSize: 1,
  flushIntervalMs: 0,
  eventThrottleMs: 0,
  persistOfflineQueue: true,
  offlineQueueKey: "geenius:analytics:offline-queue",
  replayOnReconnect: true,
  retentionDays: 365,
  respectDnt: true,
  sessionTimeout: 30 * 60 * 1000,
  excludePaths: [],
  debug: false,
  maskPii: true,
  maskIp: true,
  anonymize: false,
};

/** Options and models that customize shared cohort-analysis generation. */
export type {
  BehavioralCohortDefinition,
  BehavioralCohortResult,
  BehavioralCohortRule,
  CohortAnalysisOptions,
  CohortComparisonResult,
} from "./cohorts";
/** Shared cohort-analysis helpers used by UI variants and Convex integration. */
export {
  analyzeBehavioralCohorts,
  analyzeCohorts,
  compareBehavioralCohorts,
  createBehavioralCohortDefinition,
  createCohortRetentionPoints,
} from "./cohorts";
/** Shared configuration-bundle shape returned by the analytics config builder. */
export type { AnalyticsConfigurationBundle } from "./config";
/** Analytics configuration builders and defaults used by providers and app integrations. */
export {
  AnalyticsConfigBuilder,
  createAnalyticsConfig,
  defaultConfigs,
  getDefaultConfig,
} from "./config";
/** Shared constants consumed by package runtimes, dashboards, and review surfaces. */
export {
  DEFAULT_REALTIME_INTERVAL,
  DEFAULT_SESSION_TIMEOUT,
  EVENT_ICONS,
  MAX_EVENTS_PER_BATCH,
  MAX_FUNNEL_STEPS,
  METRIC_FORMATS,
  PROVIDERS,
  TREND_COLORS,
} from "./constants";
/** Dashboard widget and layout contracts shared across every rendered variant. */
/** Convex row shape accepted by the shared event-normalization helper. */
export type {
  ChartDashboardWidget,
  CohortDashboardWidget,
  ConvexAnalyticsEvent,
  DashboardLayout,
  DashboardWidget,
  DashboardWidgetBase,
  DashboardWidgetKind,
  DeviceBreakdownDashboardWidget,
  EventDashboardWidget,
  FunnelDashboardWidget,
  MetricDashboardWidget,
  ReplayDashboardWidget,
  RealtimeDashboardWidget,
  TopPagesDashboardWidget,
} from "./dashboard";
/** Shared dashboard composition helpers used by the package's UI surfaces. */
/** Shared helpers for materializing dashboard aggregates from raw analytics records. */
export {
  buildAnalyticsDashboardData,
  buildRealtimeStats,
  createDashboardBuilder,
  createDashboardLayout,
  normalizeAnalyticsEvents,
} from "./dashboard";
/** Typed analytics error classes shared across framework bindings and backend consumers. */
export {
  AdapterUnsupportedError,
  AnalyticsConfigurationError,
  AnalyticsContextError,
  AnalyticsError,
  AnalyticsProviderError,
} from "./errors";
/** Shared funnel-analysis helpers for conversion-oriented analytics features. */
export { analyzeFunnel, createFunnelSteps } from "./funnel";
/** Privacy helpers that sanitize analytics payloads before they reach providers or storage. */
export {
  createConsentSettings,
  filterRetainedAnalyticsAggregates,
  filterRetainedAnalyticsRecords,
  getRetentionCutoffTimestamp,
  hasSensitiveKeys,
  hashString,
  hasConsentFor,
  isDoNotTrackEnabled,
  maskString,
  pseudonymizeIdentifier,
  privacyUtils,
  sanitizeAnalyticsEvent,
  sanitizeObject,
  sanitizePageView,
  sanitizeUserTraits,
  shouldPurgeByRetention,
  truncateIpAddress,
} from "./privacy";
/** Google Analytics provider implementation. */
export { GoogleAnalyticsProvider } from "./providers/google-analytics";
/** Provider interfaces published for consumers implementing custom analytics backends. */
export type {
  AnalyticsProvider,
  AnalyticsProviderConfig,
  AnalyticsProviderName,
} from "./providers/interface";
/** Base analytics provider implementation for custom provider authors. */
export { BaseAnalyticsProvider } from "./providers/interface";
/** Mixpanel provider implementation. */
export { MixpanelProvider } from "./providers/mixpanel";
/** Plausible provider implementation. */
export { PlausibleProvider } from "./providers/plausible";
/** PostHog provider implementation. */
export { PostHogProvider } from "./providers/posthog";
/** Shared session-replay helpers for building playback snapshots from analytics activity. */
export { buildSessionReplay, replaySessionFrames } from "./session-replay";
/** Public tracker contract implemented by the shared tracker factory. */
export type {
  TrackerConfig,
  TrackerConfigInput,
  TrackerInstance,
  TrackerTrackOptions,
} from "./tracker";
/** Shared tracker factory used by providers, hooks, and standalone consumers. */
export { createTracker } from "./tracker";
/** Zod-based shared validators published for advanced runtime integrations and tests. */
export {
  AnalyticsEventSchema,
  CohortAnalysisSchema,
  CohortBucketSchema,
  CohortRetentionPointSchema,
  ConsentSchema,
  DeviceInfoSchema,
  FunnelStepSchema,
  PageViewSchema,
  PrivacySettingsSchema,
  ProviderConfigSchema,
  SessionReplayFrameSchema,
  SessionReplaySnapshotSchema,
  TimeRangeSchema,
  UserTraitsSchema,
} from "./validators";
